/* eslint-disable max-lines */
import {
  PaymentDataLike,
  PaymentHandler,
  PaymentHandlerContext,
  PaymentHandlerResult,
  PaymentRequestBody,
  PaymentsHandlerRegistry,
} from './payments-interceptor.models';

const SCHEME_BBAN = 'BBAN';
const SCHEME_IBAN = 'IBAN';

const captureForCallType = (callType: PaymentHandlerContext['callType']): PaymentHandlerResult =>
  callType === 'validate'
    ? { responseCapture: { captureFee: true } }
    : { responseCapture: { captureStatus: true, captureAdditions: true } };

const captureForOwnAccountPaymentOrder = (): PaymentHandlerResult => ({
  responseCapture: { captureFee: true, captureAdditions: true },
});

const getTransferTransactionInformation = (body: PaymentRequestBody): Record<string, any> | null =>
  body?.transferTransactionInformation ?? null;

const deleteEntryClassIfPresent = (body: PaymentRequestBody): void => {
  if ('entryClass' in body) {
    delete body.entryClass;
  }
};

const handleAirtime: PaymentHandler = (body, context, helpers) => {
  const transferTransactionInformation = getTransferTransactionInformation(body);
  if (!transferTransactionInformation) return;

  const phoneNumber = context.paymentData?.counterparty?.accountNumber;
  const providerId = context.paymentData?.counterparty?.name;

  transferTransactionInformation.counterpartyAccount.identification.identification = `254${phoneNumber}`;

  const additions = helpers.buildAdditions(context.paymentData, context.currencies);
  additions.providerId = providerId;

  helpers.setTransferTransactionInformationAdditions(transferTransactionInformation, additions);
  helpers.setTransferTransactionInformation(body, transferTransactionInformation);

  return captureForCallType(context.callType);
};

const handleIntraPayValidate: PaymentHandler = (body, context, helpers) => {
  const transferTransactionInformation = getTransferTransactionInformation(body);
  if (!transferTransactionInformation) return;

  const additions = helpers.buildAdditions(context.paymentData, context.currencies);
  if (Object.keys(additions).length > 0) {
    helpers.setTransferTransactionInformationAdditions(transferTransactionInformation, additions);
  }

  const accountName = context.paymentData?.counterparty?.accountName;
  if (accountName) {
    transferTransactionInformation.counterparty.name = accountName;
  }
  transferTransactionInformation.counterparty.role = 'CREDITOR';

  if (transferTransactionInformation.counterpartyAccount.selectedContact) {
    delete transferTransactionInformation.counterpartyAccount.selectedContact;
  }

  helpers.setTransferTransactionInformation(body, transferTransactionInformation);
  return { responseCapture: { captureStatus: true, captureFee: true } };
};

const handleIntraPayPaymentOrder: PaymentHandler = (body, context, helpers) => {
  const transferTransactionInformation = getTransferTransactionInformation(body);
  if (!transferTransactionInformation) return;

  const additions = helpers.buildAdditions(context.paymentData, context.currencies);
  if (Object.keys(additions).length > 0) {
    helpers.setTransferTransactionInformationAdditions(transferTransactionInformation, additions);
  }

  helpers.setTransferTransactionInformation(body, transferTransactionInformation);
  return captureForCallType(context.callType);
};

const setMpesaAdditions = (
  transferTransactionInformation: Record<string, any>,
  paymentData: PaymentDataLike,
  body: PaymentRequestBody,
  isPaymentOrder: boolean,
): Record<string, unknown> => {
  const originatorAccount = body.originatorAccount;
  const identification = paymentData?.counterparty?.accountNumber;
  const additions: Record<string, unknown> = {};
  const counterPartyType = paymentData?.counterparty?.counterPartyType;
  const networkProvider = isPaymentOrder
    ? paymentData?.counterparty?.networkProvider
    : paymentData?.counterparty?.provider;

  if (counterPartyType === 'mobilePhone') {
    transferTransactionInformation.counterpartyAccount.identification.identification = `254${identification}`;
    additions.mobileMoneyType = networkProvider;
  } else if (counterPartyType === 'buyGoods') {
    transferTransactionInformation.counterpartyAccount.identification.identification = paymentData?.counterparty?.tillNumber;
    additions.mobileMoneyType = 'mpesaBuyGoods';
  } else if (counterPartyType === 'payBill') {
    transferTransactionInformation.counterpartyAccount.identification.identification = paymentData?.counterparty?.paybillNumber;
    additions.payBillAccount = identification;
    additions.mobileMoneyType = 'mpesaPayBill';
  }

  if (paymentData?.initiator?.counterPartyType === 'ownAccount') {
    if (!isPaymentOrder && originatorAccount?.name) {
      transferTransactionInformation.counterparty.name = originatorAccount.name;
    }
    transferTransactionInformation.counterpartyAccount.identification.identification = `254${identification}`;
    transferTransactionInformation.counterpartyAccount.identification.mobileNumber = `254${identification}`;
    additions.mobileMoneyType = 'mpesaMobilePhone';
    additions.billAccountRef = originatorAccount?.identification?.identification;
    additions.mpesaType = 'C2B';
    if (isPaymentOrder) {
      additions.txnNarrative = 'Account Top Up';
    }
  }

  if (paymentData?.initiator?.counterPartyType === 'otherAccount') {
    if (!isPaymentOrder && originatorAccount?.name) {
      transferTransactionInformation.counterparty.name = originatorAccount.name;
    }
    transferTransactionInformation.counterpartyAccount.identification.identification = `254${identification}`;
    transferTransactionInformation.counterpartyAccount.identification.mobileNumber = `254${identification}`;
    additions.mobileMoneyType = 'mpesaMobilePhone';
    additions.billAccountRef = paymentData?.initiator?.otherAccountNumber;
    additions.mpesaType = 'C2B-OTHERACCOUNT';
    if (isPaymentOrder) {
      additions.txnNarrative = 'Account Top Up';
    }
  }

  return additions;
};

const handleMpesa: PaymentHandler = (body, context, helpers) => {
  const transferTransactionInformation = getTransferTransactionInformation(body);
  if (!transferTransactionInformation) return;

  const isPaymentOrder = context.callType === 'paymentOrder';
  const additions = setMpesaAdditions(transferTransactionInformation, context.paymentData, body, isPaymentOrder);

  transferTransactionInformation.counterpartyAccount.identification.schemeName = SCHEME_BBAN;
  helpers.setTransferTransactionInformationAdditions(transferTransactionInformation, additions);
  helpers.setTransferTransactionInformation(body, transferTransactionInformation);
  deleteEntryClassIfPresent(body);

  return captureForCallType(context.callType);
};

const handleOwnAccountValidate: PaymentHandler = (body, context, helpers) => {
  const transferTransactionInformation = getTransferTransactionInformation(body);
  if (!transferTransactionInformation) return;

  const additions = helpers.buildAdditions(context.paymentData, context.currencies);

  helpers.setTransferRemittanceInformation(transferTransactionInformation, context.paymentData);
  helpers.setTransferTransactionInformationAdditions(transferTransactionInformation, additions);
  helpers.setTransferTransactionInformation(body, transferTransactionInformation);

  return { responseCapture: { captureFee: true } };
};

const handleOwnAccountPaymentOrder: PaymentHandler = (body, context, helpers) => {
  const transferTransactionInformation = getTransferTransactionInformation(body);
  if (!transferTransactionInformation) return;

  const additions = helpers.buildAdditions(context.paymentData, context.currencies);

  helpers.setTransferRemittanceInformation(transferTransactionInformation, context.paymentData);
  helpers.setTransferTransactionInformationAdditions(transferTransactionInformation, additions);
  helpers.setTransferTransactionInformation(body, transferTransactionInformation);

  return captureForOwnAccountPaymentOrder();
};

const handlePesaLink: PaymentHandler = (body, context, helpers) => {
  const transferTransactionInformation = getTransferTransactionInformation(body);
  if (!transferTransactionInformation) return;

  const isPhoneRecipient = context.paymentData?.counterparty?.counterPartyType === 'Phone';
  const identification = isPhoneRecipient
    ? `254${context.paymentData?.counterparty?.phoneNumber}`
    : context.paymentData?.counterparty?.accountNumber;

  helpers.setTransferRemittanceInformation(transferTransactionInformation, context.paymentData);

  transferTransactionInformation.counterpartyAccount.identification.identification = identification;
  transferTransactionInformation.counterpartyAccount.identification.schemeName = SCHEME_BBAN;

  const additions = helpers.buildAdditions(context.paymentData, context.currencies);
  additions.sendTo = isPhoneRecipient ? 'MOBILE_NUMBER' : 'ACCOUNT_NUMBER';
  additions.recipientMobileNo = identification;
  additions.recipientBankCode = context.paymentData?.counterparty?.bankBranchCode;
  if (context.callType === 'validate') {
    additions.providerId = context.paymentData?.counterparty?.name;
  }

  helpers.setTransferTransactionInformationAdditions(transferTransactionInformation, additions);
  helpers.setTransferTransactionInformation(body, transferTransactionInformation);

  if (context.callType === 'paymentOrder') {
    deleteEntryClassIfPresent(body);
  }

  return captureForCallType(context.callType);
};

const handleRtgs: PaymentHandler = (body, context, helpers) => {
  const transferTransactionInformation = getTransferTransactionInformation(body);
  if (!transferTransactionInformation) return;

  const additions = helpers.buildAdditions(context.paymentData, context.currencies);
  additions.rtgsCode = context.paymentData?.counterparty?.rtgsCode?.rtgsCode;
  additions.beneficiaryStreet = context.paymentData?.counterparty?.beneficiaryStreet;
  additions.beneficiaryCntryTown = context.paymentData?.counterparty?.beneficiaryCntryTown;

  helpers.setTransferRemittanceInformation(transferTransactionInformation, context.paymentData);
  transferTransactionInformation.counterpartyAccount.identification.schemeName = SCHEME_BBAN;
  transferTransactionInformation.counterparty.postalAddress = {
    addressLine1: context.paymentData?.counterparty?.beneficiaryAddress,
  };
  transferTransactionInformation.counterpartyBank.bankBranchCode = context.paymentData?.counterparty?.bankBranchCode;
  transferTransactionInformation.chargeBearer = 'SHA';

  helpers.setTransferTransactionInformationAdditions(transferTransactionInformation, additions);
  helpers.setTransferTransactionInformation(body, transferTransactionInformation);

  if (context.callType === 'paymentOrder') {
    deleteEntryClassIfPresent(body);
  }

  return context.callType === 'validate'
    ? { responseCapture: { captureFee: true, captureAdditions: true } }
    : captureForCallType(context.callType);
};

const handleTreasury: PaymentHandler = (body, context, helpers) => {
  const transferTransactionInformation = getTransferTransactionInformation(body);
  if (!transferTransactionInformation) return;

  body.paymentType = 'SBK_RTGS_PAYMENT';
  transferTransactionInformation.remittanceInformation = '';
  transferTransactionInformation.counterpartyAccount.identification.schemeName = SCHEME_BBAN;
  transferTransactionInformation.counterpartyAccount.identification.identification = context.paymentData?.counterparty?.paymentKey;
  transferTransactionInformation.counterparty.postalAddress = { addressLine1: '' };
  transferTransactionInformation.chargeBearer = 'SHA';

  const additions = helpers.buildAdditions(context.paymentData, context.currencies);
  additions.rtgsCode = context.paymentData?.counterparty?.rtgsCode;
  additions.settlementDate = context.paymentData?.counterparty?.settlementDate;
  additions.providerId = 'DHOWCSD';
  additions.billAccountRef = context.paymentData?.counterparty?.paymentKey;

  helpers.setTransferTransactionInformationAdditions(transferTransactionInformation, additions);
  helpers.setTransferTransactionInformation(body, transferTransactionInformation);

  if (context.callType === 'paymentOrder') {
    deleteEntryClassIfPresent(body);
  }

  return captureForCallType(context.callType);
};

const handleFundUnitTrust: PaymentHandler = (body, context, helpers) => {
  const transferTransactionInformation = getTransferTransactionInformation(body);
  if (!transferTransactionInformation) return;

  const fundingSource = context.paymentData?.initiator?.fundingSource;
  const phoneNumber = context.paymentData?.initiator?.phoneNumber;
  const mobileProvider = context.paymentData?.initiator?.mobileProvider;
  const currency = context.paymentData?.remittanceInfo?.amountGroup?.currency;
  const shortCode = context.paymentData?.counterparty?.shortCode;
  const currencyCode = shortCode;
  const identification =
    fundingSource === 'phone'
      ? `254${phoneNumber}`
      : `${currencyCode}${context.paymentData?.counterparty?.clientIdentifier}`;

  body.paymentType = fundingSource === 'phone' ? 'SBK_MPESA' : 'SBK_INTRA_PAY';

  transferTransactionInformation.remittanceInformation = '';
  transferTransactionInformation.counterpartyAccount.identification.schemeName = SCHEME_BBAN;
  transferTransactionInformation.counterpartyAccount.identification.identification = identification;
  transferTransactionInformation.counterparty.postalAddress = { addressLine1: '' };

  const additions: Record<string, unknown> = {};
  if (fundingSource === 'phone') {
    transferTransactionInformation.counterpartyAccount.identification.mobileNumber = `254${phoneNumber}`;
    additions.mobileMoneyType = mobileProvider;
    additions.mpesaType = 'C2B-OTHERACCOUNT';
    additions.billAccountRef = phoneNumber;
    additions.txnNarrative = 'Account Top Up';
  } else {
    additions.rtgsCode = context.paymentData?.counterparty?.fundShareClassCode || 'MMF';
  }

  helpers.setTransferTransactionInformationAdditions(transferTransactionInformation, additions);
  helpers.setTransferTransactionInformation(body, transferTransactionInformation);

  if (context.callType === 'paymentOrder') {
    deleteEntryClassIfPresent(body);
  }

  return captureForCallType(context.callType);
};

const handleWithdrawUnitTrust: PaymentHandler = (body, context, helpers) => {
  const transferTransactionInformation = getTransferTransactionInformation(body);
  if (!transferTransactionInformation) return;

  const clientIdentifier = context.paymentData?.counterparty?.clientIdentifier;
  const fundShareClassCode = context.paymentData?.counterparty?.fundShareClassCode;

  body.paymentType = 'SBK_INTRA_PAY';

  transferTransactionInformation.remittanceInformation = '';
  transferTransactionInformation.counterpartyAccount.identification.schemeName = SCHEME_BBAN;
  transferTransactionInformation.counterpartyAccount.identification.identification = clientIdentifier;

  const additions: Record<string, unknown> = {
    billAccountRef: clientIdentifier,
    rtgsCode: fundShareClassCode,
  };

  helpers.setTransferTransactionInformationAdditions(transferTransactionInformation, additions);
  helpers.setTransferTransactionInformation(body, transferTransactionInformation);

  if (context.callType === 'paymentOrder') {
    deleteEntryClassIfPresent(body);
  }

  return captureForCallType(context.callType);
};

const applySwiftCountrySpecificAdditions = (
  additions: Record<string, unknown>,
  paymentData: PaymentDataLike,
): void => {
  let specialField: string | undefined;
  let purposeCode: string | undefined;

  switch (paymentData?.counterparty?.country?.country) {
    case 'US':
      specialField = paymentData?.counterparty?.fedWireABANumber?.toString();
      break;
    case 'IN':
      specialField = paymentData?.counterparty?.neft;
      purposeCode = paymentData?.counterparty?.neft;
      break;
    case 'AU':
    case 'CA':
      specialField = paymentData?.counterparty?.bsbNumber?.toString();
      break;
    case 'CN':
      purposeCode = paymentData?.counterparty?.chinaPurposeCodes;
      break;
    case 'AE':
      purposeCode = paymentData?.counterparty?.uaePurposeCodes;
      break;
    default:
      break;
  }

  if (specialField) {
    additions.specialField = specialField;
  }

  if (purposeCode) {
    additions.purposeCode = purposeCode;
  }
};

const BILLER_FALLBACKS: Record<string, { billType: string; billerCode: string; billerName: string }> = {
  DSTV: { billType: 'POST-PAID', billerCode: 'PAYU', billerName: 'DSTV' },
  CCARD: { billType: 'PRE-PAID', billerCode: 'BANK', billerName: 'Card Payment' },
  GOTV: { billType: 'POST-PAID', billerCode: 'PAYU', billerName: 'GOTV' },
  NBI_WATER: { billType: 'POST-PAID', billerCode: 'IPAY', billerName: 'NBI Water' },
  ZUKU: { billType: 'POST-PAID', billerCode: 'ZUKU', billerName: 'ZUKU' },
  KRA: { billType: 'POST-PAID', billerCode: 'KRA', billerName: 'KRA' },
  POWERBILL: { billType: 'POST-PAID', billerCode: 'KPLC', billerName: 'KPLC Postpaid' },
  MOJA: { billType: 'PRE-PAID', billerCode: 'MOJA', billerName: 'Nairobi ExpressWay' },
};

const getSelectedBillerProviderId = (
  paymentData: PaymentDataLike,
  toCamelCaseKey: (value: string) => string,
): string => {
  const counterparty = paymentData?.counterparty ?? {};
  const explicitProviderId = counterparty.providerId || counterparty.billerName?.providerId;
  if (explicitProviderId) {
    return explicitProviderId;
  }

  const selectedBillerValue = counterparty.billerName?.billerName || counterparty.name || '';
  switch (toCamelCaseKey(selectedBillerValue)) {
    case 'dstv':
      return 'DSTV';
    case 'cardPayment':
      return 'CCARD';
    case 'gotv':
      return 'GOTV';
    case 'nbiWater':
      return 'NBI_WATER';
    case 'zuku':
      return 'ZUKU';
    case 'kra':
      return 'KRA';
    case 'powerBill':
      return 'POWERBILL';
    case 'nairobiExpressway':
      return 'MOJA';
    default:
      return selectedBillerValue;
  }
};

const handleSwift: PaymentHandler = (body, context, helpers) => {
  const transferTransactionInformation = getTransferTransactionInformation(body);
  if (!transferTransactionInformation) return;

  const additions = helpers.buildAdditions(context.paymentData, context.currencies);
  applySwiftCountrySpecificAdditions(additions, context.paymentData);
  additions.beneficiaryStreet = context.paymentData?.counterparty?.postalAddress?.beneficiaryStreet;
  additions.beneficiaryCntryTown = context.paymentData?.counterparty?.postalAddress?.beneficiaryCntryTown;

  helpers.setTransferRemittanceInformation(transferTransactionInformation, context.paymentData);
  transferTransactionInformation.counterparty.postalAddress.country = context.paymentData?.counterparty?.country?.country;
  transferTransactionInformation.counterpartyAccount.identification.schemeName = SCHEME_IBAN;

  if (transferTransactionInformation.counterpartyBank) {
    transferTransactionInformation.counterpartyBank.name =
      transferTransactionInformation.counterpartyBank.name || context.paymentData?.counterparty?.swift;
    transferTransactionInformation.counterpartyBank.bankBranchCode =
      transferTransactionInformation.counterpartyBank.bankBranchCode || context.paymentData?.counterparty?.swift;
  } else {
    transferTransactionInformation.counterpartyBank = {
      name: context.paymentData?.counterparty?.swift,
      bankBranchCode: context.paymentData?.counterparty?.swift,
    };
  }

  helpers.setTransferTransactionInformationAdditions(transferTransactionInformation, additions);
  helpers.setTransferTransactionInformation(body, transferTransactionInformation);

  if (context.callType === 'paymentOrder') {
    deleteEntryClassIfPresent(body);
  }

  return captureForCallType(context.callType);
};

const configureBillerAdditions = (
  additions: Record<string, unknown>,
  paymentData: PaymentDataLike,
  body: PaymentRequestBody,
  transferTransactionInformation: Record<string, any>,
  toCamelCaseKey: (value: string) => string,
): void => {
  const providerId = getSelectedBillerProviderId(paymentData, toCamelCaseKey);
  const fallback = BILLER_FALLBACKS[providerId];
  const billerName = paymentData?.counterparty?.name || fallback?.billerName || '';
  const billType = paymentData?.counterparty?.billType || fallback?.billType;
  const billerCode = paymentData?.counterparty?.billerCode || fallback?.billerCode;
  const billerAccountNumber = paymentData?.counterparty?.accountNumber;
  const topUpType = paymentData?.counterparty?.topUpType;
  const obuCardNumber = paymentData?.counterparty?.obuCardNumber;
  const plateNumber = paymentData?.counterparty?.plateNumber;
  const mtcCardNumber = paymentData?.counterparty?.mtcCardNumber;
  const mojaIdNumber = paymentData?.counterparty?.idNumber;

  if (providerId === 'MOJA') {
    const selectedAccount = topUpType === 'obu' ? obuCardNumber : topUpType === 'plateNumber' ? plateNumber : mtcCardNumber;
    paymentData.paymentType = 'SBK_MOJA_PAYMENT';
    body.paymentType = 'SBK_MOJA_PAYMENT';
    if (paymentData.counterparty) {
      paymentData.counterparty.accountNumber = selectedAccount;
    }
    transferTransactionInformation.counterpartyAccount.identification.identification = selectedAccount;
    additions.providerId = providerId;
    additions.billAccountName = billerName;
    additions.cardEtcNumber = selectedAccount;
    additions.cardEtcType =
      topUpType === 'obu' ? 'ETC.NUMBER' : topUpType === 'plateNumber' ? 'ETC.VEHICLE.NO' : 'CARD.NUMBER';
    additions.chargeType = paymentData?.counterparty?.userType;
    additions.idPinNumber = mojaIdNumber;
    return;
  }

  if (fallback || providerId) {
    additions.providerId = providerId;
    additions.billType = billType;
    additions.billerCode = providerId === 'NBI_WATER' ? 'IPAY' : billerCode;
    additions.billerName = billerName;
    additions.billAccountRef = billerAccountNumber;

    if (providerId === 'NBI_WATER') {
      additions.billAccountName = billerName;
    }
  }
};

const handleBillPayment: PaymentHandler = (body, context, helpers) => {
  const transferTransactionInformation = getTransferTransactionInformation(body);
  if (!transferTransactionInformation) return;

  const additions = helpers.buildAdditions(context.paymentData, context.currencies);
  configureBillerAdditions(additions, context.paymentData, body, transferTransactionInformation, helpers.toCamelCaseKey);

  transferTransactionInformation.counterpartyAccount.identification.schemeName = SCHEME_BBAN;
  helpers.setTransferTransactionInformationAdditions(transferTransactionInformation, additions);
  helpers.setTransferTransactionInformation(body, transferTransactionInformation);
  deleteEntryClassIfPresent(body);

  return captureForCallType(context.callType);
};

const handleMojaPaymentOrder: PaymentHandler = (body, context, helpers) => {
  const transferTransactionInformation = getTransferTransactionInformation(body);
  if (!transferTransactionInformation) return;

  const additions = helpers.buildAdditions(context.paymentData, context.currencies);
  configureBillerAdditions(additions, context.paymentData, body, transferTransactionInformation, helpers.toCamelCaseKey);

  transferTransactionInformation.counterpartyAccount.identification.schemeName = SCHEME_BBAN;
  helpers.setTransferTransactionInformationAdditions(transferTransactionInformation, additions);
  helpers.setTransferTransactionInformation(body, transferTransactionInformation);
  deleteEntryClassIfPresent(body);

  return captureForCallType(context.callType);
};

export const buildPaymentsHandlerRegistry = (): PaymentsHandlerRegistry => ({
  'SBK_AIRTIME:validate': handleAirtime,
  'SBK_AIRTIME:paymentOrder': handleAirtime,
  'SBK_INTRA_PAY:validate': handleIntraPayValidate,
  'SBK_INTRA_PAY:paymentOrder': handleIntraPayPaymentOrder,
  'SBK_MPESA:validate': handleMpesa,
  'SBK_MPESA:paymentOrder': handleMpesa,
  'SBK_OWN_ACTR:validate': handleOwnAccountValidate,
  'SBK_OWN_ACTR:paymentOrder': handleOwnAccountPaymentOrder,
  'SBK_PESALINK_PAYMENT:validate': handlePesaLink,
  'SBK_PESALINK_PAYMENT:paymentOrder': handlePesaLink,
  'SBK_RTGS_PAYMENT:validate': handleRtgs,
  'SBK_RTGS_PAYMENT:paymentOrder': handleRtgs,
  'SBK_TREASURY:validate': handleTreasury,
  'SBK_TREASURY:paymentOrder': handleTreasury,
  'SBK_FUND_UNIT_TRUST_ACCOUNT:validate': handleFundUnitTrust,
  'SBK_FUND_UNIT_TRUST_ACCOUNT:paymentOrder': handleFundUnitTrust,
  'SBK_WITHDRAW_FROM_UNIT_TRUST_ACCOUNT:validate': handleWithdrawUnitTrust,
  'SBK_WITHDRAW_FROM_UNIT_TRUST_ACCOUNT:paymentOrder': handleWithdrawUnitTrust,
  'SBK_SWIFT_PAYMENT:validate': handleSwift,
  'SBK_SWIFT_PAYMENT:paymentOrder': handleSwift,
  'SBK_BILL_PAYMENT:validate': handleBillPayment,
  'SBK_BILL_PAYMENT:paymentOrder': handleBillPayment,
  'SBK_MOJA_PAYMENT:paymentOrder': handleMojaPaymentOrder,
});
